/**
 * 桑基图数据清洗：ECharts 的 sankey 只支持有向无环图（DAG）。
 * 接口连线里只要出现自环（A→A）或反向边（A→B 与 B→A 同时存在、节点被跨层复用），
 * echarts/lib/chart/sankey/sankeyLayout.js 的 Kahn 拓扑排序就会抛
 * "Sankey is a DAG, the original data has cycle!"，整张图渲染中断（页面该区域空白）。
 *
 * 清洗顺序：
 * 1. 合并重边：同一 source→target 的多条连线合并为一条，value 累加（如 12 条 胃脘痛→胃脘痛 → 1 条 value 12）；
 * 2. 剔除自环：source === target 的连线全部丢弃，避免自身死循环；
 * 3. 破除双向环：按 value 从大到小贪心保留，「权重大者优先」，会让已保留的图成环的反向边直接跳过，结果必为 DAG；
 * 4. 自动提取节点：从清洗后的连线中提取并去重所有节点，不再要求接口手动维护 nodes 数组。
 *
 * 端点解析与 ECharts 保持一致（data/Graph.js 的 generateNodeKey + addEdge）：节点身份为 id > name > 下标，
 * 连线 source / target 为数字时按「节点下标」解析；输出统一为节点名，由 ECharts 按名称匹配。
 */
const DEFAULT_EDGE_VALUE = 1;

const asArray = (value) => (Array.isArray(value) ? value : []);

// 与 createGraphFromNodeEdge 的 retrieve(node.id, node.name, i) 保持一致
function nodeKeyOf(node, index) {
    if (node && node.id != null && node.id !== '') return String(node.id);
    if (node && node.name != null && node.name !== '') return String(node.name);
    return String(index);
}

function edgeValueOf(link) {
    const value = Number(link && link.value);
    return Number.isFinite(value) ? value : DEFAULT_EDGE_VALUE;
}

// 数字按下标解析（与 ECharts 一致，越界时退化为按名称解析），其余按名称解析；空端点视为无效连线
function resolveEndpoint(endpoint, dataNodes) {
    if (endpoint == null || endpoint === '') return null;
    if (typeof endpoint === 'number') {
        const node = dataNodes[endpoint];
        return node ? nodeKeyOf(node, endpoint) : String(endpoint);
    }
    return String(endpoint);
}

/**
 * @param {Object} sankey 接口数据 { data: [], links: [] }；data 可选，仅用于同列节点排序与节点附加配置
 * @return {{ nodes: Array, links: Array, mergedCount: number, selfLoopCount: number, cycleCount: number, invalidCount: number }}
 *   nodes：从清洗后连线自动提取并去重的节点；links：合并重边、剔除自环、破除双向环后的连线；
 *   其余为各类被处理连线的条数，仅用于开发环境提示。
 */
export function sanitizeSankey(sankey) {
    const dataNodes = asArray(sankey && sankey.data);
    const rawLinks = asArray(sankey && sankey.links);
    const result = {
        nodes: [],
        links: [],
        mergedCount: 0,
        selfLoopCount: 0,
        cycleCount: 0,
        invalidCount: 0,
    };
    if (!rawLinks.length) return result;

    // 1. 合并重边：value 累加，保留第一条连线的其余配置（如 lineStyle）
    const mergedMap = new Map();
    const merged = [];
    rawLinks.forEach((link) => {
        const source = resolveEndpoint(link && link.source, dataNodes);
        const target = resolveEndpoint(link && link.target, dataNodes);
        if (source == null || target == null) {
            result.invalidCount += 1;
            return;
        }
        const key = source + '\u0000' + target;
        const exists = mergedMap.get(key);
        if (exists) {
            exists.value += edgeValueOf(link);
            result.mergedCount += 1;
            return;
        }
        const item = { ...link, source, target, value: edgeValueOf(link) };
        mergedMap.set(key, item);
        merged.push(item);
    });

    // 2. 剔除自环
    const candidates = merged.filter((link) => {
        if (link.source === link.target) {
            result.selfLoopCount += 1;
            return false;
        }
        return true;
    });

    // 3. 破除双向环：权重降序贪心保留，遇到会成环的反向边直接跳过
    const kept = new Array(candidates.length).fill(false);
    const adjacency = new Map();
    const canReach = (from, to) => {
        const stack = [from];
        const seen = new Set();
        while (stack.length) {
            const name = stack.pop();
            if (name === to) return true;
            if (seen.has(name)) continue;
            seen.add(name);
            (adjacency.get(name) || []).forEach((next) => stack.push(next));
        }
        return false;
    };
    candidates
        .map((link, index) => ({ link, index }))
        .sort((a, b) => b.link.value - a.link.value || a.index - b.index)
        .forEach(({ link, index }) => {
            // target 已能沿保留的连线回到 source，说明这是回边/反向边，保留它会成环
            if (canReach(link.target, link.source)) {
                result.cycleCount += 1;
                return;
            }
            kept[index] = true;
            if (!adjacency.has(link.source)) adjacency.set(link.source, []);
            adjacency.get(link.source).push(link.target);
        });
    result.links = candidates.filter((link, index) => kept[index]);

    // 4. 从清洗后的连线自动提取节点并去重；接口 data 只作为同列排序提示与节点附加配置来源
    const dataNodeMap = new Map();
    const dataOrder = new Map();
    dataNodes.forEach((node, index) => {
        const key = nodeKeyOf(node, index);
        if (!dataNodeMap.has(key)) {
            dataNodeMap.set(key, node);
            dataOrder.set(key, index);
        }
    });
    const firstAppear = new Map();
    result.links.forEach((link) => {
        [link.source, link.target].forEach((name) => {
            if (!firstAppear.has(name)) firstAppear.set(name, firstAppear.size);
        });
    });
    result.nodes = Array.from(firstAppear.keys())
        .sort((a, b) => {
            // 同列节点的上下顺序由 nodes 数组顺序决定（sankeyLayout 的 initializeNodeDepth），
            // 因此接口 data 里出现过的节点按原顺序排在前面，只在连线里出现的节点按首次出现顺序补齐
            const orderA = dataOrder.has(a) ? dataOrder.get(a) : Number.MAX_SAFE_INTEGER;
            const orderB = dataOrder.has(b) ? dataOrder.get(b) : Number.MAX_SAFE_INTEGER;
            return orderA - orderB || firstAppear.get(a) - firstAppear.get(b);
        })
        .map((name) => dataNodeMap.get(name) || { name });
    return result;
}
